// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

export interface EmailTemplate {
  id?: string;
  templateType: 'ADVISOR_LOA_PROGRESS' | 'ADVISOR_LOA_COMPLETED' | 'PROVIDER_LOA_NOTIFICATION';
  templateLevel: 'DEFAULT' | 'BROKER' | 'PROVIDER' | 'POLICY' | 'BROKER_PROVIDER' | 'BROKER_POLICY';
  brokerageId?: string;
  brokerageName?: string;
  providerId?: string;
  providerName?: string;
  policyTypeId?: string;
  policyTypeName?: string;
  subjectTemplate: string;
  bodyTemplateHtml: string;
  bodyTemplateText?: string;
  isActive: boolean;
  version?: number;
  displayName?: string;
  createdBy?: string;
  createdAt?: string;
  updatedBy?: string;
  updatedAt?: string;
}

export interface EmailTemplateCreateDTO {
  templateType: 'ADVISOR_LOA_PROGRESS' | 'ADVISOR_LOA_COMPLETED' | 'PROVIDER_LOA_NOTIFICATION';
  templateLevel: 'DEFAULT' | 'BROKER' | 'PROVIDER' | 'POLICY' | 'BROKER_PROVIDER' | 'BROKER_POLICY';
  brokerageId?: string;
  providerId?: string;
  policyTypeId?: string;
  subjectTemplate: string;
  bodyTemplateHtml: string;
  bodyTemplateText?: string;
  isActive: boolean;
}

export interface RenderedEmail {
  subject: string;
  bodyHtml: string;
  bodyText?: string;
}

const API_BASE = '/api/admin/email-templates';

export const emailTemplateApi = {
  /**
   * List email templates with optional filtering
   */
  async getAll(params?: {
    templateType?: string;
    brokerageId?: string;
    providerId?: string;
    policyTypeId?: string;
  }): Promise<EmailTemplate[]> {
    const queryParams = new URLSearchParams();
    if (params?.templateType) queryParams.append('templateType', params.templateType);
    if (params?.brokerageId) queryParams.append('brokerageId', params.brokerageId);
    if (params?.providerId) queryParams.append('providerId', params.providerId);
    if (params?.policyTypeId) queryParams.append('policyTypeId', params.policyTypeId);

    const queryString = queryParams.toString();
    const suffix = queryString ? `?${queryString}` : '';
    const url = `${API_BASE}${suffix}`;
    const response = await fetch(url, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch email templates');
    }
    return response.json();
  },

  /**
   * Get email template by ID
   */
  async getById(id: string): Promise<EmailTemplate> {
    const response = await fetch(`${API_BASE}/${id}`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch email template');
    }
    return response.json();
  },

  /**
   * Create new email template
   */
  async create(dto: EmailTemplateCreateDTO): Promise<EmailTemplate> {
    const response = await fetch(API_BASE, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(dto)
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || 'Failed to create email template');
    }
    return response.json();
  },

  /**
   * Update existing email template
   */
  async update(id: string, dto: EmailTemplateCreateDTO): Promise<EmailTemplate> {
    const response = await fetch(`${API_BASE}/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(dto)
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || 'Failed to update email template');
    }
    return response.json();
  },

  /**
   * Delete email template
   */
  async delete(id: string): Promise<void> {
    const response = await fetch(`${API_BASE}/${id}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to delete email template');
    }
  },

  /**
   * Preview email template with test variables
   */
  async preview(templateId: string, testVariables: Record<string, string>): Promise<RenderedEmail> {
    const response = await fetch(`${API_BASE}/preview?templateId=${templateId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(testVariables)
    });
    if (!response.ok) {
      throw new Error('Failed to preview email template');
    }
    return response.json();
  },

  /**
   * Resolve provider template (for testing hierarchy)
   */
  async resolveProviderTemplate(params: {
    templateType: string;
    brokerageId?: string;
    providerId?: string;
    policyTypeId?: string;
  }): Promise<EmailTemplate> {
    const queryParams = new URLSearchParams();
    queryParams.append('templateType', params.templateType);
    if (params.brokerageId) queryParams.append('brokerageId', params.brokerageId);
    if (params.providerId) queryParams.append('providerId', params.providerId);
    if (params.policyTypeId) queryParams.append('policyTypeId', params.policyTypeId);

    const response = await fetch(`${API_BASE}/resolve/provider?${queryParams}`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to resolve provider template');
    }
    return response.json();
  },

  /**
   * Resolve advisor template (for testing hierarchy)
   */
  async resolveAdvisorTemplate(params: {
    templateType: string;
    brokerageId?: string;
  }): Promise<EmailTemplate> {
    const queryParams = new URLSearchParams();
    queryParams.append('templateType', params.templateType);
    if (params.brokerageId) queryParams.append('brokerageId', params.brokerageId);

    const response = await fetch(`${API_BASE}/resolve/advisor?${queryParams}`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to resolve advisor template');
    }
    return response.json();
  }
};
